import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/ui/Icon";

export interface SelectOption {
  value: string;
  /** 화면 표시 라벨 — 미지정 시 value 사용 */
  label?: string;
  /** 옵션과 선택값의 라벨 텍스트에 동일하게 적용할 클래스 */
  labelClassName?: string;
}

export interface SelectGroup {
  label: string;
  options: (SelectOption | string)[];
}

interface SelectProps {
  /** 현재 선택값 (controlled). 옛 코드 호환: defaultValue 도 사용 가능 */
  value?: string;
  defaultValue?: string;
  /** 평탄 옵션 — 단순 1차원 select */
  options?: (SelectOption | string)[];
  /** 그룹화 옵션 — optgroup 같은 효과 */
  groups?: SelectGroup[];
  onChange?: (value: string) => void;
  disabled?: boolean;
  /** 트리거 width 자동 vs 풀 */
  fullWidth?: boolean;
  /** 변형 스타일용 클래스 — 루트와 portal 메뉴 양쪽에 함께 부여된다 */
  className?: string;
  /** 메뉴를 열었을 때 내부에서 라벨·값 검색 */
  searchable?: boolean;
  searchPlaceholder?: string;
  ariaLabel?: string;
  /** 선택값이 옵션에 없을 때(빈 값 등) 트리거에 흐리게 보일 안내 문구 — 예: "근무 법인을 고르세요" */
  placeholder?: string;
}

function normOpt(o: SelectOption | string): SelectOption {
  return typeof o === "string" ? { value: o } : o;
}

export function Select({
  value,
  defaultValue,
  options,
  groups,
  onChange,
  disabled,
  fullWidth = true,
  className,
  searchable = false,
  searchPlaceholder = "검색…",
  ariaLabel,
  placeholder,
}: SelectProps) {
  const [internal, setInternal] = useState(value ?? defaultValue ?? "");
  const isControlled = value !== undefined;
  const cur = isControlled ? value : internal;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const listId = useId();
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; minWidth: number } | null>(null);

  // 외부 클릭 → 닫기. portal로 떠 있는 menu도 "내부"로 인정.
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // 위치 측정 + 스크롤/리사이즈 대응
  useEffect(() => {
    if (!open) return;
    function updatePos() {
      const r = ref.current?.getBoundingClientRect();
      if (!r) return;
      setMenuPos({ top: r.bottom + 4, left: r.left, minWidth: r.width });
    }
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [open]);

  /** 평탄/그룹 옵션을 합쳐 현재값에 해당하는 라벨 lookup */
  const optionMap = useMemo(() => {
    const m = new Map<string, SelectOption>();
    for (const o of options ?? []) {
      const n = normOpt(o);
      m.set(n.value, n);
    }
    for (const g of groups ?? []) {
      for (const o of g.options) {
        const n = normOpt(o);
        m.set(n.value, n);
      }
    }
    return m;
  }, [options, groups]);

  const selectedOption = optionMap.get(cur);
  const displayLabel = selectedOption?.label ?? cur ?? "";
  const normalize = (text: string) => text.normalize("NFKC").toLowerCase().replace(/\s+/g, "");
  const needle = searchable ? normalize(query) : "";
  const matches = (o: SelectOption | string) => {
    const n = normOpt(o);
    return !needle || normalize(`${n.label ?? ""} ${n.value}`).includes(needle);
  };
  const visibleOptions = options?.filter(matches);
  const visibleGroups = groups?.map((g) => ({ ...g, options: g.options.filter(matches) })).filter((g) => g.options.length);
  const hasResults = !!visibleOptions?.length || !!visibleGroups?.length;

  function toggle() {
    if (disabled) return;
    if (!open) {
      const r = ref.current?.getBoundingClientRect();
      if (r) setMenuPos({ top: r.bottom + 4, left: r.left, minWidth: r.width });
      setQuery("");
    }
    setOpen(!open);
  }

  function onMenuKey(e: KeyboardEvent<HTMLDivElement>) {
    if (e.nativeEvent.isComposing) return;
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    } else if (searchable && e.key === "Tab") {
      setOpen(false);
      triggerRef.current?.focus();
    } else if (searchable && ["ArrowDown", "ArrowUp", "Enter"].includes(e.key)) {
      const nodes = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>(".select-opt") ?? []);
      const index = nodes.indexOf(document.activeElement as HTMLButtonElement);
      if (e.key === "Enter") {
        if (index < 0 && nodes[0]) { e.preventDefault(); nodes[0].click(); }
      } else {
        e.preventDefault();
        const next = e.key === "ArrowDown" ? (index + 1) % nodes.length : (index < 0 ? nodes.length - 1 : (index - 1 + nodes.length) % nodes.length);
        nodes[next]?.focus();
      }
    }
  }

  function pick(v: string) {
    if (!isControlled) setInternal(v);
    onChange?.(v);
    setOpen(false);
    if (searchable) triggerRef.current?.focus();
  }

  return (
    <div
      className={`select ${open ? "is-open" : ""} ${disabled ? "is-disabled" : ""}${className ? ` ${className}` : ""}`}
      ref={ref}
      style={fullWidth ? { width: "100%" } : undefined}
    >
      <button
        type="button"
        ref={triggerRef}
        className="select-trigger"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? listId : undefined}
        onClick={toggle}
        onKeyDown={(e) => {
          if (open && e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            setOpen(false);
          }
        }}
        disabled={disabled}
      >
        {!selectedOption && placeholder ? (
          <span className="select-placeholder">{placeholder}</span>
        ) : (
          <span className={selectedOption?.labelClassName}>{displayLabel}</span>
        )}
        <Icon name="chevD" size={14} />
      </button>
      {open && menuPos && createPortal(
        <div
          ref={menuRef}
          onKeyDown={onMenuKey}
          className={`select-menu is-portal${className ? ` ${className}` : ""}`}
          style={{
            position: "fixed",
            top: menuPos.top,
            left: menuPos.left,
            minWidth: menuPos.minWidth,
          }}
        >
          {searchable && (
            <div className="select-search">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                aria-label={ariaLabel ? `${ariaLabel} 검색` : "옵션 검색"}
                aria-controls={listId}
                autoFocus
                autoComplete="off"
              />
            </div>
          )}
          <div id={listId} role="listbox" aria-label={ariaLabel}>
          {visibleOptions?.map((o) => {
            const n = normOpt(o);
            return (
              <button
                key={n.value}
                type="button"
                role="option"
                aria-selected={n.value === cur}
                className={`select-opt ${n.value === cur ? "is-active" : ""}`}
                onClick={() => pick(n.value)}
              >
                <span className={n.labelClassName}>{n.label ?? n.value}</span>
              </button>
            );
          })}
          {visibleGroups?.map((g) => (
            <div key={g.label} className="select-group">
              <div className="select-group-hd">{g.label}</div>
              {g.options.map((o) => {
                const n = normOpt(o);
                return (
                  <button
                    key={n.value}
                    type="button"
                    role="option"
                    aria-selected={n.value === cur}
                    className={`select-opt ${n.value === cur ? "is-active" : ""}`}
                    onClick={() => pick(n.value)}
                  >
                    <span className={n.labelClassName}>{n.label ?? n.value}</span>
                  </button>
                );
              })}
            </div>
          ))}
          </div>
          {searchable && !hasResults && <div className="select-empty" role="status">검색 결과가 없습니다.</div>}
        </div>,
        document.body,
      )}
    </div>
  );
}
